import { type V2, angleTo, dirFromAngle, dist, normalize, rotate } from '../core/math';
import { Rng } from '../core/rng';
import { Actor, type ActorInit, type CastState } from './actor';
import { type HitResult, computeHit, hardCCAllowed, pushDir, reactionAllowed, COUNTERED_DURATION, STAGGERED_DURATION } from './combat';
import type { SimEvent } from './events';
import { separate, updateMovement } from './movement';
import type { NavGrid } from './navGrid';
import { registry } from './registry';
import { type Placement, scaleShape, shapeHits, shapeReach } from './shapes';
import {
  castCancelable,
  finishSequence,
  INPUT_BUFFER,
  releaseCast,
  startCast,
  updateCast,
  type CastBlock,
} from './skillRuntime';
import type { AreaSpec, HitSpec, ProjectileSpec, ResolvedSkill, SkillAction, StatusApply } from './skillTypes';
import { SpatialHash } from './spatialHash';
import { applyStatus, recomputeStats, removeStatus, updateStatuses } from './statusSystem';
import { getStatus } from './status';
import { SHARED_ID_LIMIT } from './ids';
import type { ActionKey, Element, EntityId, PeerId, Team } from './types';

export interface Projectile {
  id: number;
  ownerId: EntityId;
  team: Team;
  castId: number;
  skill: ResolvedSkill | null;
  spec: ProjectileSpec;
  pos: V2;
  dir: V2;
  traveled: number;
  returning: boolean;
  hitIds: Set<EntityId>;
  mult: number;
  dead: boolean;
}

export interface AreaEffect {
  id: number;
  ownerId: EntityId;
  team: Team;
  castId: number;
  skill: ResolvedSkill | null;
  spec: AreaSpec;
  placement: Placement;
  t: number;
  erupted: boolean;
  tickTimer: number;
  mult: number;
  dead: boolean;
}

/** Hit forwarded to the peer that owns the target. */
export interface HitClaim {
  toPeer: PeerId;
  result: HitResult;
}

export type Brain = (world: World, a: Actor, dt: number) => void;

export interface WorldOptions {
  seed?: number | string;
  nav: NavGrid;
  localPeer?: PeerId;
  hostPeer?: PeerId;
  partySize?: number;
  pvp?: boolean;
}

const brains = new Map<string, Brain>();
export function registerBrain(id: string, brain: Brain): void {
  brains.set(id, brain);
}

export class World {
  time = 0;
  tickCount = 0;
  readonly rng: Rng;
  readonly actors = new Map<EntityId, Actor>();
  projectiles: Projectile[] = [];
  areas: AreaEffect[] = [];
  events: SimEvent[] = [];
  outbox: HitClaim[] = [];
  nav: NavGrid;
  readonly spatial = new SpatialHash<Actor>(4);
  localPeer: PeerId;
  hostPeer: PeerId;
  partySize: number;
  pvp: boolean;
  /** Latest block reason from a failed cast (for UI feedback). */
  lastCastBlock: CastBlock | null = null;
  /** Aim point per locally controlled actor (mouse ground position). */
  private aims = new Map<EntityId, V2>();
  private idCounter = 1;
  private castCounter = 1;
  private fxCounter = 1;
  /** Cached alive list rebuilt each tick. */
  aliveActors: Actor[] = [];
  /** Extra per-tick hooks (zone directors, encounter scripts). */
  readonly tickHooks: ((world: World, dt: number) => void)[] = [];

  constructor(opts: WorldOptions) {
    this.rng = new Rng(opts.seed ?? 1);
    this.nav = opts.nav;
    this.localPeer = opts.localPeer ?? 'local';
    this.hostPeer = opts.hostPeer ?? this.localPeer;
    this.partySize = opts.partySize ?? 1;
    this.pvp = opts.pvp ?? false;
  }

  // ------------------------------------------------------------------ ids / entities

  get isHost(): boolean {
    return this.localPeer === this.hostPeer;
  }

  allocId(): EntityId {
    return this.idCounter++;
  }

  /** Reserve ids above this value (used when syncing host-assigned ids). */
  bumpIds(min: number): void {
    if (this.idCounter <= min) this.idCounter = min + 1;
  }

  nextCastId(): number {
    return this.castCounter++ * 1000 + (this.localPeerHash % 1000);
  }

  private get localPeerHash(): number {
    let h = 0;
    for (let i = 0; i < this.localPeer.length; i++) h = (h * 31 + this.localPeer.charCodeAt(i)) | 0;
    return Math.abs(h);
  }

  private nextFxId(): number {
    return this.fxCounter++ * 1000 + (this.localPeerHash % 1000);
  }

  addActor(init: ActorInit | Actor): Actor {
    const a = init instanceof Actor ? init : new Actor(init);
    this.actors.set(a.id, a);
    // Only host-allocated ids advance the counter (peer-local and player ids live above it).
    if (a.id < SHARED_ID_LIMIT) this.bumpIds(a.id);
    this.emit({ type: 'spawn', id: a.id });
    return a;
  }

  removeActor(id: EntityId): void {
    if (!this.actors.has(id)) return;
    this.actors.delete(id);
    this.aims.delete(id);
    this.emit({ type: 'despawn', id });
  }

  isLocal(a: Actor): boolean {
    return a.owner === this.localPeer;
  }

  /** Switches this peer's id (e.g. 'local' → network id) and re-owns its actors. */
  rebindLocalPeer(id: string): void {
    const old = this.localPeer;
    if (old === id) return;
    for (const a of this.actors.values()) if (a.owner === old) a.owner = id;
    if (this.hostPeer === old) this.hostPeer = id;
    this.localPeer = id;
  }

  emit(ev: SimEvent): void {
    this.events.push(ev);
  }

  drainEvents(): SimEvent[] {
    const e = this.events;
    this.events = [];
    return e;
  }

  drainOutbox(): HitClaim[] {
    const o = this.outbox;
    this.outbox = [];
    return o;
  }

  setAim(a: Actor, p: V2): void {
    this.aims.set(a.id, { ...p });
  }

  aimOf(a: Actor): V2 | undefined {
    return this.aims.get(a.id);
  }

  isEnemy(a: { team: Team }, b: { team: Team }): boolean {
    if (a.team === 'neutral' || b.team === 'neutral') return false;
    return a.team !== b.team;
  }

  // ------------------------------------------------------------------ player input

  /** Click-to-move with pathfinding. */
  moveTo(a: Actor, goal: V2): void {
    if (!a.alive) return;
    a.moveDir = null;
    if (a.cast && !castCancelable(a.cast, false)) {
      a.moveGoal = { ...goal };
      a.path = this.nav.findPath(a.pos, goal) ?? [];
      return;
    }
    if (a.cast) this.endCast(a, true);
    a.moveGoal = { ...goal };
    a.path = this.nav.findPath(a.pos, goal) ?? [];
  }

  stop(a: Actor): void {
    a.path = [];
    a.moveGoal = null;
    a.moveDir = null;
  }

  /** Resolves which skill a key triggers for a player (identity may override slots). */
  skillForKey(a: Actor, key: ActionKey): string | null {
    const p = a.player;
    if (!p) return null;
    const cls = registry.cls(p.classId);
    switch (key) {
      case 'SPACE':
        return cls.identity.dashFor ? cls.identity.dashFor(a, p.dash) : p.dash;
      case 'V':
        return p.awakening;
      case 'BASIC':
        return p.basic;
      case 'Z':
      case 'X':
        return null;
      default: {
        const idx = 'QWERASDF'.indexOf(key);
        const base = p.bar[idx] ?? null;
        return cls.identity.skillForSlot ? cls.identity.skillForSlot(a, idx, base) : base;
      }
    }
  }

  /** Key press for a locally controlled player. Returns true if something happened. */
  pressKey(a: Actor, key: ActionKey, aim?: V2): boolean {
    const p = a.player;
    if (!p || !a.alive) return false;
    if (aim) this.setAim(a, aim);
    const aimPoint = this.aimOf(a) ?? { x: a.pos.x + Math.sin(a.facing), z: a.pos.z + Math.cos(a.facing) };

    if (key === 'SPACE' && (a.downTimer > 0 || a.airTimer > 0)) return this.standUp(a);
    if (key === 'Z' || key === 'X') {
      const cls = registry.cls(p.classId);
      return cls.identity.onKey?.(this, a, key) ?? false;
    }

    const skillId = this.skillForKey(a, key);
    if (!skillId) return false;
    const skill = a.skills.get(skillId);
    if (!skill) return false;
    return this.useSkill(a, skill, key, aimPoint);
  }

  /**
   * Uses a skill as if its key was pressed: continues combos, honours
   * follow-up windows and cancel windows, and buffers early inputs. Identity
   * controllers call this for their Z/X skills.
   */
  useSkill(a: Actor, skill: ResolvedSkill, key: ActionKey, aimPoint?: V2): boolean {
    const p = a.player;
    if (!p || !a.alive) return false;
    const aim = aimPoint ?? this.aimOf(a) ?? { x: a.pos.x + Math.sin(a.facing), z: a.pos.z + Math.cos(a.facing) };

    // Continue a combo within the current cast.
    if (a.cast && a.cast.skill.id === skill.id && (skill.castType === 'combo' || skill.castType === 'chain')) {
      if (a.cast.stage + 1 < skill.stages.length) {
        a.cast.queued = true;
        return true;
      }
      // Final stage: the sequence's cooldown only starts when it ends, so a re-press must not restart it for free.
      if (skill.cooldown > 0 && !(skill.charges && skill.charges > 1)) return false;
    }
    // Follow-up window after the previous stage ended.
    if (p.followUp && p.followUp.skillId === skill.id && this.time <= p.followUp.until) {
      if (a.cast && !castCancelable(a.cast, false)) {
        p.buffered = { key, until: this.time + INPUT_BUFFER, aim };
        return true;
      }
      const stage = p.followUp.stage;
      p.followUp.stagesUsed++;
      p.seqUsed = p.followUp.stagesUsed;
      p.followUp = null;
      return !!startCast(this, a, skill, aim, { key, stage });
    }
    const byDash = skill.kind === 'dash';
    if (a.cast && !castCancelable(a.cast, byDash)) {
      p.buffered = { key, until: this.time + INPUT_BUFFER, aim };
      return true;
    }
    // Casting something else closes any pending follow-up window.
    if (p.followUp) this.expireFollowUp(a);
    const c = startCast(this, a, skill, aim, { key });
    if (c && skill.kind === 'dash') this.emit({ type: 'dash', actorId: a.id });
    if (c && (skill.castType === 'combo' || skill.castType === 'chain')) p.seqUsed = 1;
    return !!c;
  }

  releaseKey(a: Actor, key: ActionKey): void {
    releaseCast(a, key);
  }

  private expireFollowUp(a: Actor): void {
    const p = a.player;
    if (!p?.followUp) return;
    const s = a.skills.get(p.followUp.skillId);
    if (s) finishSequence(this, a, s, p.followUp.stagesUsed);
    p.followUp = null;
  }

  standUp(a: Actor): boolean {
    const p = a.player;
    if (!p || p.standUpCd > 0) return false;
    a.downTimer = 0;
    a.airTimer = 0;
    a.height = 0;
    a.displacement = null;
    p.standUpCd = this.pvp ? 15 : 30;
    this.invuln(a, 0.5);
    applyStatus(this, a, { id: 'statusImmune', duration: 0.8 }, a.id, 0);
    this.emit({ type: 'standUp', actorId: a.id });
    return true;
  }

  invuln(a: Actor, seconds: number): void {
    a.invulnUntil = Math.max(a.invulnUntil, this.time + seconds);
  }

  /** Replays a cast started by another peer (visuals + victim-side hits). */
  replayCast(a: Actor, skillId: string, stage: number, aimAngle: number, aimPoint: V2, castId: number, phase: CastState['phase']): void {
    let skill = a.skills.get(skillId);
    if (!skill) {
      const def = registry.skills.get(skillId);
      if (!def) return;
      skill = { ...def, level: 1, dmgMult: 1, critRateAdd: 0, critDmgAdd: 0, atkSpeedMult: 1, vsWeakMult: 1, vsBossMult: 1, defPen: 0, damageReduction: 0, identityGainMult: 1, extraStatus: [], tripods: [] };
      a.skills.set(skillId, skill);
    }
    if (a.cast && a.cast.castId === castId && a.cast.stage === stage && phase !== 'stage') return;
    if (a.cast && a.cast.castId === castId && phase === 'stage' && a.cast.stage === stage && a.cast.phase === 'stage') return;
    if (a.cast && a.cast.castId === castId) {
      // Same cast advancing (casting -> stage, holding -> finisher, next combo stage).
      a.cast.held = false;
      a.cast.stage = stage;
      a.cast.aimAngle = aimAngle;
      a.cast.aimPoint = aimPoint;
      if (phase === 'stage' && a.cast.phase !== 'stage') {
        // Let the local runtime transition naturally by marking release.
        return;
      }
    }
    startCast(this, a, skill, aimPoint, { remote: true, stage, castId, aimAngle });
  }

  endCast(a: Actor, interrupted: boolean, keepSequence = false): void {
    const c = a.cast;
    if (!c) return;
    a.cast = null;
    this.emit({ type: 'castEnd', actorId: a.id, castId: c.castId, interrupted });
    if (interrupted) this.cancelCastAreas(a.id, c.castId);
    const s = c.skill;
    if (a.player && this.isLocal(a) && (s.castType === 'combo' || s.castType === 'chain') && !keepSequence) {
      finishSequence(this, a, s, a.player.seqUsed || c.stage + 1);
      a.player.seqUsed = 0;
      a.player.followUp = null;
    }
  }

  /**
   * Drops the still-pending `cancelOnInterrupt` areas spawned by one cast (runs
   * on every peer that sees the cast end interrupted).
   */
  cancelCastAreas(ownerId: EntityId, castId: number): void {
    for (const ar of this.areas) {
      if (ar.dead || ar.erupted || ar.ownerId !== ownerId || ar.castId !== castId || !ar.spec.cancelOnInterrupt) continue;
      ar.dead = true;
      this.emit({ type: 'areaEnd', id: ar.id });
    }
  }

  interruptCast(a: Actor): void {
    if (a.cast) this.endCast(a, true);
  }

  noteStageUsed(a: Actor, _s: ResolvedSkill): void {
    if (a.player) a.player.seqUsed = (a.player.seqUsed || 1) + 1;
  }

  // ------------------------------------------------------------------ actions

  fireAction(a: Actor, c: CastState, action: SkillAction, split: number): void {
    const s = c.skill;
    const local = this.isLocal(a);
    const mult = c.mult * split;
    const placementSelf: Placement = { origin: { ...a.pos }, facing: c.aimAngle };
    switch (action.kind) {
      case 'hit': {
        const h = c.areaScale !== 1 ? { ...action.hit, shape: scaleShape(action.hit.shape, c.areaScale) } : action.hit;
        const placement = h.anchor === 'point' ? { origin: { ...c.aimPoint }, facing: c.aimAngle } : placementSelf;
        this.emit({ type: 'action', actorId: a.id, castId: c.castId, skillId: s.id, kind: 'hit', vfx: h.vfx, origin: placement.origin, facing: placement.facing, shape: h.shape, element: h.element ?? s.element, hue: s.hue });
        this.resolveShape(a, s, h, placement, mult);
        break;
      }
      case 'move': {
        this.emit({ type: 'action', actorId: a.id, castId: c.castId, skillId: s.id, kind: 'move', origin: { ...a.pos }, facing: c.aimAngle, hue: s.hue });
        if (!local) break;
        let distance = action.distance;
        if (action.toPoint) distance = Math.min(Math.abs(action.distance), dist(a.pos, c.aimPoint));
        const dir = dirFromAngle(c.aimAngle);
        const sign = action.distance < 0 ? -1 : 1;
        const v = (Math.abs(distance) * sign) / Math.max(0.01, action.duration);
        a.displacement = { vx: dir.x * v, vz: dir.z * v, remaining: action.duration, ignoreCollision: !!action.ignoreCollision, voluntary: true };
        break;
      }
      case 'teleport': {
        this.emit({ type: 'action', actorId: a.id, castId: c.castId, skillId: s.id, kind: 'teleport', origin: { ...a.pos }, facing: c.aimAngle, hue: s.hue });
        if (!local) break;
        let target: V2;
        if (action.toPoint) {
          const d = Math.min(action.distance, dist(a.pos, c.aimPoint));
          const dir = dirFromAngle(c.aimAngle);
          target = { x: a.pos.x + dir.x * d, z: a.pos.z + dir.z * d };
        } else {
          const dir = dirFromAngle(c.aimAngle);
          target = { x: a.pos.x + dir.x * action.distance, z: a.pos.z + dir.z * action.distance };
        }
        target = this.nav.clampToBounds(target);
        a.pos = this.nav.walkable(target) ? target : (this.nav.nearestWalkable(target) ?? a.pos);
        a.prevPos = { ...a.pos };
        break;
      }
      case 'projectile':
        this.spawnProjectiles(a, s, c, action.proj, mult);
        break;
      case 'area': {
        const spec = c.areaScale !== 1 ? { ...action.area, shape: scaleShape(action.area.shape, c.areaScale), hit: { ...action.area.hit, shape: scaleShape(action.area.hit.shape, c.areaScale) } } : action.area;
        this.spawnArea(a, s, c.castId, spec, c.aimAngle, c.aimPoint, mult);
        break;
      }
      case 'self':
        if (local) for (const st of action.status) applyStatus(this, a, st, a.id, a.stats.attack);
        this.emit({ type: 'action', actorId: a.id, castId: c.castId, skillId: s.id, kind: 'self', origin: { ...a.pos }, facing: a.facing, hue: s.hue });
        break;
      case 'party': {
        this.emit({ type: 'action', actorId: a.id, castId: c.castId, skillId: s.id, kind: 'party', origin: { ...a.pos }, facing: a.facing, shape: { kind: 'circle', radius: action.radius }, hue: s.hue });
        for (const ally of this.aliveActors) {
          if (ally.kind !== 'player' || this.isEnemy(a, ally)) continue;
          if (ally === a && action.includeSelf === false) continue;
          if (dist(ally.pos, a.pos) > action.radius) continue;
          // Victim-side: each peer applies party buffs to the players it owns.
          if (!this.isLocal(ally)) continue;
          for (const st of action.status) applyStatus(this, ally, st, a.id, a.stats.attack);
        }
        break;
      }
      case 'invuln':
        if (local) this.invuln(a, action.duration);
        break;
      case 'counterWindow':
        // Runs on every peer: the attacker's copy judges counters.
        this.openCounterWindow(a, action.duration);
        break;
      case 'announce':
        this.emit({ type: 'announce', text: action.text, style: action.style ?? 'mechanic', actorId: a.id });
        break;
      case 'identity': {
        if (!local || !a.player) break;
        const cls = registry.cls(a.player.classId);
        cls.identity.op?.(this, a, action.op, action.value ?? 0);
        this.emit({ type: 'identity', actorId: a.id, op: action.op });
        break;
      }
    }
  }

  // ------------------------------------------------------------------ hit resolution

  /**
   * Routing rule. Effects on players are resolved by the player's owner
   * (victim-side: dodges are judged on the dodger's screen). Effects on
   * everything else are resolved by the attacker's owner and forwarded to the
   * entity's owner as a claim (attacker-side: hits feel instant).
   */
  shouldResolve(src: Actor, target: Actor): boolean {
    if (target.kind === 'player') return this.isLocal(target);
    return this.isLocal(src);
  }

  /** Candidate targets for a shape, filtered by team and liveness. */
  queryTargets(src: Actor, placement: Placement, hit: HitSpec): Actor[] {
    const reach = shapeReach(hit.shape) + 3;
    const cands = this.spatial.query(placement.origin, reach);
    const out: Actor[] = [];
    for (const t of cands) {
      if (!t.alive || t.kind === 'npc') continue;
      if (hit.friendly) {
        if (this.isEnemy(src, t)) continue;
      } else if (!this.isEnemy(src, t)) continue;
      if (!shapeHits(hit.shape, placement, t.pos, t.radius)) continue;
      out.push(t);
    }
    if (hit.maxTargets && out.length > hit.maxTargets) {
      out.sort((x, y) => dist(x.pos, placement.origin) - dist(y.pos, placement.origin));
      out.length = hit.maxTargets;
    }
    return out;
  }

  resolveShape(src: Actor, skill: ResolvedSkill | null, hit: HitSpec, placement: Placement, mult: number, exclude?: Set<EntityId>): Actor[] {
    // Purely visual actions (coef 0 and no side effects) never touch targets.
    if (!hit.friendly && hit.coef <= 0 && !hit.status?.length && !hit.reaction) return [];
    const hitList = this.queryTargets(src, placement, hit);
    const out: Actor[] = [];
    for (const t of hitList) {
      if (exclude?.has(t.id)) continue;
      out.push(t);
      if (!this.shouldResolve(src, t)) continue;
      const res = computeHit(src, t, { skill, hit, mult, from: placement.origin }, () => this.rng.next());
      this.deliver(res);
    }
    return out;
  }

  /** Applies a result locally or forwards it to the owning peer. */
  deliver(res: HitResult): void {
    const target = this.actors.get(res.tgtId);
    if (!target) return;
    if (this.isLocal(target)) {
      this.applyHitResult(res);
    } else {
      this.outbox.push({ toPeer: target.owner, result: res });
      if (!res.friendly) this.predictRemoteHit(target, res);
    }
  }

  /**
   * Attacker-side bookkeeping for a hit forwarded to another peer: immediate
   * feedback, kill credit and identity gain. The owner's result stays
   * authoritative for the target itself.
   */
  private predictRemoteHit(target: Actor, res: HitResult): void {
    this.emit({ type: 'hit', srcId: res.srcId, tgtId: res.tgtId, amount: res.amount, crit: res.crit, back: res.back, head: res.head, counter: res.counter, staggered: target.hasStatus('staggered'), heal: false, shield: false, dot: res.isDot, element: res.element, absorbed: 0, kill: false, predicted: true });
    if (target.monster) target.monster.threat.set(res.srcId, (target.monster.threat.get(res.srcId) ?? 0) + res.amount);
    if (!res.isDot && res.reaction && res.reaction.kind !== 'none' && reactionAllowed(target, res.reaction)) this.emit({ type: 'reaction', tgtId: target.id, kind: res.reaction.kind });
    const src = this.actors.get(res.srcId);
    if (src?.player && !res.isDot && this.isLocal(src)) {
      registry.classes.get(src.player.classId)?.identity.onDamageDealt?.(this, src, res.skillId ? (registry.skills.get(res.skillId) ?? null) : null, res.amount, res.counter);
    }
  }

  /** Authoritative application of a hit on a locally-owned target. */
  applyHitResult(res: HitResult): void {
    const t = this.actors.get(res.tgtId);
    if (!t || !t.alive) return;
    const src = this.actors.get(res.srcId) ?? null;

    if (res.friendly) {
      for (const id of res.cleanse ?? []) removeStatus(this, t, id);
      if (res.heal > 0) this.heal(t, res.heal, res.srcId);
      if (res.shield > 0) this.applyShield(t, res.shield, res.shieldDuration || 4, res.srcId);
      for (const st of res.statuses) applyStatus(this, t, st, res.srcId, res.srcAttack);
      return;
    }
    if (this.time < t.invulnUntil || t.hasStatus('invuln')) return;
    // Victim-side identity hooks may negate the hit outright (parry stances).
    if (t.player && registry.classes.get(t.player.classId)?.identity.onIncomingHit?.(this, t, res)) return;

    let amount = res.amount;
    // Skill damage reduction while casting (e.g. "Sturdy Armor" facets).
    if (t.cast?.skill.damageReduction) amount *= 1 - t.cast.skill.damageReduction;
    amount = Math.round(amount);

    // Counters interrupt the boss before damage is shown.
    if (res.counter && t.monster && t.monster.counterUntil > 0) {
      t.monster.counterUntil = 0;
      t.monster.countered = COUNTERED_DURATION;
      this.interruptCast(t);
      this.emit({ type: 'counter', srcId: res.srcId, tgtId: t.id });
      res.stagger *= 1.5;
    }

    const absorbed = this.absorbShield(t, amount);
    amount -= absorbed;
    t.hp -= amount;
    t.combatIdle = 0;
    if (src) src.combatIdle = 0;
    if (t.monster) {
      t.monster.idle = 0;
      t.monster.threat.set(res.srcId, (t.monster.threat.get(res.srcId) ?? 0) + res.amount);
    }
    // Sleep breaks on damage.
    for (const st of [...t.statuses]) if (getStatus(st.id).cc?.breakOnDamage) removeStatus(this, t, st.id);

    const kill = t.hp <= 0;
    this.emit({ type: 'hit', srcId: res.srcId, tgtId: t.id, amount, crit: res.crit, back: res.back, head: res.head, counter: res.counter, staggered: t.hasStatus('staggered'), heal: false, shield: false, dot: res.isDot, element: res.element, absorbed, kill, predicted: false });

    if (src?.player && !res.isDot) {
      const cls = registry.classes.get(src.player.classId);
      if (cls && this.isLocal(src)) cls.identity.onDamageDealt?.(this, src, res.skillId ? (registry.skills.get(res.skillId) ?? null) : null, res.amount, res.counter);
    }

    if (kill) {
      this.kill(t, src);
      return;
    }

    if (!res.isDot) {
      this.applyStagger(t, res.stagger, res.srcId);
      this.applyWeakPoint(t, res.weakPoint, res.srcId);
      for (const st of res.statuses) applyStatus(this, t, st, res.srcId, res.srcAttack);
      if (res.reaction) this.applyReaction(t, res.reaction, res.from);
    }
  }

  applyDot(t: Actor, src: Actor | null, amount: number, element: Element): void {
    if (!t.alive || this.time < t.invulnUntil) return;
    const dmg = Math.max(1, Math.round(amount * (1 + t.stats.mods.dmgTakenPct) * (1 - t.stats.dmgReduction)));
    const absorbed = this.absorbShield(t, dmg);
    t.hp -= dmg - absorbed;
    const kill = t.hp <= 0;
    this.emit({ type: 'hit', srcId: src?.id ?? 0, tgtId: t.id, amount: dmg - absorbed, crit: false, back: false, head: false, counter: false, staggered: t.hasStatus('staggered'), heal: false, shield: false, dot: true, element, absorbed, kill, predicted: false });
    if (kill) this.kill(t, src);
  }

  heal(t: Actor, amount: number, srcId: EntityId): void {
    if (!t.alive || amount <= 0) return;
    const healed = Math.min(t.stats.maxHp - t.hp, amount * t.stats.healingReceivedMult);
    if (healed <= 0) return;
    t.hp += healed;
    this.emit({ type: 'hit', srcId, tgtId: t.id, amount: Math.round(healed), crit: false, back: false, head: false, counter: false, staggered: false, heal: true, shield: false, dot: false, element: 'none', absorbed: 0, kill: false, predicted: false });
  }

  applyShield(t: Actor, amount: number, duration: number, srcId: EntityId): void {
    if (!t.alive) return;
    const existing = t.statuses.find((s) => s.id === 'shield');
    if (existing) {
      existing.shield = Math.max(existing.shield ?? 0, amount);
      existing.remaining = Math.max(existing.remaining, duration);
    } else {
      t.statuses.push({ id: 'shield', sourceId: srcId, sourceAttack: 0, remaining: duration, duration, stacks: 1, value: 0, tickTimer: 0, shield: amount });
      this.emit({ type: 'status', tgtId: t.id, statusId: 'shield', on: true });
    }
    t.shield = t.statuses.reduce((sum, s) => sum + (s.shield ?? 0), 0);
    this.emit({ type: 'hit', srcId, tgtId: t.id, amount: Math.round(amount), crit: false, back: false, head: false, counter: false, staggered: false, heal: false, shield: true, dot: false, element: 'none', absorbed: 0, kill: false, predicted: false });
  }

  private absorbShield(t: Actor, amount: number): number {
    let left = amount;
    for (const s of t.statuses) {
      if (!s.shield || s.shield <= 0) continue;
      const take = Math.min(s.shield, left);
      s.shield -= take;
      left -= take;
      if (left <= 0) break;
    }
    t.shield = t.statuses.reduce((sum, s) => sum + (s.shield ?? 0), 0);
    return amount - left;
  }

  applyReaction(t: Actor, r: NonNullable<HitResult['reaction']>, from: V2): void {
    if (!reactionAllowed(t, r)) return;
    const dir = pushDir(t, from);
    const power = r.power ?? 0;
    switch (r.kind) {
      case 'flinch':
        t.flinchTimer = Math.max(t.flinchTimer, 0.25);
        if (!t.player) this.interruptCast(t);
        break;
      case 'knockback':
        this.interruptCast(t);
        t.displacement = { vx: dir.x * (power / 0.22), vz: dir.z * (power / 0.22), remaining: 0.22, ignoreCollision: false, voluntary: false };
        t.flinchTimer = Math.max(t.flinchTimer, 0.3);
        break;
      case 'pull':
        this.interruptCast(t);
        t.displacement = { vx: -dir.x * (power / 0.22), vz: -dir.z * (power / 0.22), remaining: 0.22, ignoreCollision: false, voluntary: false };
        t.flinchTimer = Math.max(t.flinchTimer, 0.3);
        break;
      case 'knockdown':
        this.interruptCast(t);
        t.downTimer = Math.max(t.downTimer, r.duration ?? 1.2);
        if (power > 0) t.displacement = { vx: dir.x * (power / 0.25), vz: dir.z * (power / 0.25), remaining: 0.25, ignoreCollision: false, voluntary: false };
        break;
      case 'launch':
        this.interruptCast(t);
        t.airTotal = r.duration ?? 0.9;
        t.airTimer = t.airTotal;
        if (power > 0) t.displacement = { vx: dir.x * (power / 0.4), vz: dir.z * (power / 0.4), remaining: 0.4, ignoreCollision: false, voluntary: false };
        break;
      case 'none':
        break;
    }
    this.emit({ type: 'reaction', tgtId: t.id, kind: r.kind });
  }

  applyStagger(t: Actor, amount: number, byId: EntityId | null): void {
    const st = t.monster?.stagger;
    if (!st || !st.active || amount <= 0) return;
    st.value -= amount;
    if (st.value <= 0) {
      st.value = 0;
      st.active = false;
      st.recoverTimer = STAGGERED_DURATION + 6;
      this.interruptCast(t);
      // Staggered bosses are helpless and take extra damage; each break leaves a permanent scar.
      t.statuses = t.statuses.filter((s) => s.id !== 'staggered');
      t.statuses.push({ id: 'staggered', sourceId: byId ?? 0, sourceAttack: 0, remaining: STAGGERED_DURATION, duration: STAGGERED_DURATION, stacks: 1, value: 0, tickTimer: 0 });
      const scar = t.statuses.find((s) => s.id === 'staggerScar');
      if (scar) scar.stacks = Math.min(10, scar.stacks + 1);
      else t.statuses.push({ id: 'staggerScar', sourceId: 0, sourceAttack: 0, remaining: 1e9, duration: 1e9, stacks: 1, value: 0, tickTimer: 0 });
      t.monster!.counterUntil = 0;
      recomputeStats(t);
      this.emit({ type: 'staggerBreak', id: t.id, byId });
    }
  }

  applyWeakPoint(t: Actor, level: number, _byId: EntityId): void {
    const parts = t.monster?.parts;
    if (!parts || level <= 0) return;
    const part = parts.find((p) => !p.broken);
    if (!part) return;
    part.value -= level;
    if (part.value <= 0) {
      part.value = 0;
      part.broken = true;
      this.emit({ type: 'partBreak', id: t.id, partId: part.id, name: part.name });
    }
  }

  kill(t: Actor, killer: Actor | null): void {
    if (!t.alive) return;
    t.alive = false;
    t.hp = 0;
    t.diedAt = this.time;
    t.displacement = null;
    t.path = [];
    this.interruptCast(t);
    t.statuses = [];
    t.statsDirty = true;
    if (t.player) t.player.deaths++;
    this.emit({ type: 'death', id: t.id, killerId: killer?.id ?? null });
  }

  revive(t: Actor, hpPct = 1): void {
    t.alive = true;
    t.hp = Math.max(1, Math.round(t.stats.maxHp * hpPct));
    t.downTimer = 0;
    t.airTimer = 0;
    this.invuln(t, 2);
    this.emit({ type: 'revive', id: t.id });
  }

  /** Opens a counter window (blue glow) on a monster. */
  openCounterWindow(t: Actor, seconds: number): void {
    if (!t.monster) return;
    t.monster.counterUntil = seconds;
    this.emit({ type: 'counterWindow', id: t.id, duration: seconds });
  }

  // ------------------------------------------------------------------ projectiles & areas

  spawnProjectiles(a: Actor, s: ResolvedSkill, c: CastState, spec: ProjectileSpec, mult: number): void {
    const n = spec.count ?? 1;
    const spread = ((spec.spreadDeg ?? 0) * Math.PI) / 180;
    for (let i = 0; i < n; i++) {
      const off = n > 1 ? -spread / 2 + (spread * i) / (n - 1) : 0;
      const dir = rotate(dirFromAngle(c.aimAngle), off);
      const p: Projectile = {
        id: this.nextFxId(),
        ownerId: a.id,
        team: a.team,
        castId: c.castId,
        skill: s,
        spec,
        pos: { x: a.pos.x + dir.x * 0.6, z: a.pos.z + dir.z * 0.6 },
        dir,
        traveled: 0,
        returning: false,
        hitIds: new Set(),
        mult,
        dead: false,
      };
      this.projectiles.push(p);
      this.emit({ type: 'projectile', id: p.id, ownerId: a.id, pos: { ...p.pos }, dir, speed: spec.speed, vfx: spec.vfx, hue: s.hue, height: spec.height ?? 1.1, radius: spec.radius });
    }
  }

  spawnArea(a: Actor, s: ResolvedSkill | null, castId: number, spec: AreaSpec, facing: number, aimPoint: V2, mult: number, originOverride?: V2): AreaEffect {
    let origin: V2;
    if (originOverride) origin = { ...originOverride };
    else if (spec.anchor === 'point') origin = { ...aimPoint };
    else if (spec.anchor === 'target') {
      const tgt = a.monster?.targetId ? this.actors.get(a.monster.targetId) : undefined;
      origin = tgt ? { ...tgt.pos } : { ...aimPoint };
    } else origin = { ...a.pos };
    if (spec.offset) origin = this.offsetOrigin(origin, spec.offset, facing);
    const area: AreaEffect = {
      id: this.nextFxId(),
      ownerId: a.id,
      team: a.team,
      castId,
      skill: s,
      spec,
      placement: { origin, facing },
      t: 0,
      erupted: false,
      tickTimer: 0,
      mult,
      dead: false,
    };
    this.areas.push(area);
    this.emit({ type: 'area', id: area.id, ownerId: a.id, origin, facing, shape: spec.shape, delay: spec.delay ?? 0, duration: spec.duration ?? 0, telegraph: spec.telegraph ?? (a.kind === 'monster' ? 'red' : 'none'), friendly: !!spec.hit.friendly, vfx: spec.vfx, hue: s?.hue });
    return area;
  }

  /** Applies an `AreaSpec.offset` (cast frame: z = forward, x = sideways) to an anchor point. */
  private offsetOrigin(origin: V2, offset: V2, facing: number): V2 {
    const o = rotate(offset, facing);
    return { x: origin.x + o.x, z: origin.z + o.z };
  }

  private updateProjectiles(dt: number): void {
    for (const p of this.projectiles) {
      if (p.dead) continue;
      const owner = this.actors.get(p.ownerId);
      if (!owner) {
        p.dead = true;
        continue;
      }
      // Homing toward the nearest enemy in a cone.
      if (p.spec.homing && !p.returning) {
        let best: Actor | null = null;
        let bestD = 12;
        for (const t of this.spatial.query(p.pos, 12)) {
          if (!t.alive || !this.isEnemy(owner, t) || t.kind === 'npc') continue;
          const ang = Math.abs(Math.atan2(t.pos.x - p.pos.x, t.pos.z - p.pos.z) - Math.atan2(p.dir.x, p.dir.z));
          const d = dist(p.pos, t.pos);
          if (ang < p.spec.homing && d < bestD) {
            best = t;
            bestD = d;
          }
        }
        if (best) {
          const want = normalize({ x: best.pos.x - p.pos.x, z: best.pos.z - p.pos.z });
          p.dir = normalize({ x: p.dir.x + want.x * dt * 6, z: p.dir.z + want.z * dt * 6 });
        }
      }
      const step = p.spec.speed * dt;
      if (p.returning) {
        const back = normalize({ x: owner.pos.x - p.pos.x, z: owner.pos.z - p.pos.z });
        p.dir = back;
      }
      p.pos = { x: p.pos.x + p.dir.x * step, z: p.pos.z + p.dir.z * step };
      p.traveled += step;

      const hitShape = { kind: 'circle' as const, radius: p.spec.radius };
      const placement = { origin: p.pos, facing: Math.atan2(p.dir.x, p.dir.z) };
      const targets = this.queryTargets(owner, placement, { ...p.spec.hit, shape: hitShape });
      for (const t of targets) {
        if (p.hitIds.has(t.id)) continue;
        p.hitIds.add(t.id);
        if (p.spec.explode) {
          this.emit({ type: 'areaErupt', id: p.id, origin: { ...p.pos }, facing: placement.facing, shape: p.spec.explode.shape, vfx: p.spec.explode.vfx ?? p.spec.vfx, hue: p.skill?.hue });
          this.resolveShape(owner, p.skill, p.spec.explode, { origin: { ...p.pos }, facing: placement.facing }, p.mult);
          p.dead = true;
          break;
        }
        if (this.shouldResolve(owner, t)) {
          const res = computeHit(owner, t, { skill: p.skill, hit: p.spec.hit, mult: p.mult, from: p.pos }, () => this.rng.next());
          this.deliver(res);
        }
        if (!p.spec.pierce) {
          p.dead = true;
          break;
        }
      }
      if (p.dead) {
        this.emit({ type: 'projectileEnd', id: p.id, pos: { ...p.pos }, hit: true });
        continue;
      }
      if (!p.returning && p.traveled >= p.spec.range) {
        if (p.spec.returns) {
          p.returning = true;
          p.hitIds.clear();
        } else {
          if (p.spec.explode) {
            this.emit({ type: 'areaErupt', id: p.id, origin: { ...p.pos }, facing: placement.facing, shape: p.spec.explode.shape, vfx: p.spec.explode.vfx ?? p.spec.vfx, hue: p.skill?.hue });
            this.resolveShape(owner, p.skill, p.spec.explode, { origin: { ...p.pos }, facing: placement.facing }, p.mult);
          }
          p.dead = true;
          this.emit({ type: 'projectileEnd', id: p.id, pos: { ...p.pos }, hit: false });
        }
      } else if (p.returning && dist(p.pos, owner.pos) < 0.8) {
        p.dead = true;
        this.emit({ type: 'projectileEnd', id: p.id, pos: { ...p.pos }, hit: false });
      } else if (!this.nav.walkable(p.pos) && !this.nav.inBounds(...this.nav.cellOf(p.pos))) {
        p.dead = true;
        this.emit({ type: 'projectileEnd', id: p.id, pos: { ...p.pos }, hit: false });
      }
    }
    this.projectiles = this.projectiles.filter((p) => !p.dead);
  }

  private updateAreas(dt: number): void {
    for (const ar of this.areas) {
      if (ar.dead) continue;
      const owner = this.actors.get(ar.ownerId);
      ar.t += dt;
      if (ar.spec.followCaster && owner) ar.placement.origin = ar.spec.offset ? this.offsetOrigin(owner.pos, ar.spec.offset, ar.placement.facing) : { ...owner.pos };
      const delay = ar.spec.delay ?? 0;
      const duration = ar.spec.duration ?? 0;
      if (!owner) {
        ar.dead = true;
        continue;
      }
      if (!ar.erupted && ar.t >= delay) {
        ar.erupted = true;
        ar.tickTimer = ar.spec.tickInterval ?? duration;
        this.emit({ type: 'areaErupt', id: ar.id, origin: ar.placement.origin, facing: ar.placement.facing, shape: ar.spec.shape, vfx: ar.spec.vfx, hue: ar.skill?.hue });
        this.resolveShape(owner, ar.skill, ar.spec.hit, ar.placement, ar.mult);
      } else if (ar.erupted && duration > 0 && ar.spec.tickInterval) {
        ar.tickTimer -= dt;
        if (ar.tickTimer <= 0) {
          ar.tickTimer += ar.spec.tickInterval;
          this.resolveShape(owner, ar.skill, ar.spec.hit, ar.placement, ar.mult);
        }
      }
      if (ar.erupted && ar.spec.pull && ar.t < delay + duration) {
        for (const t of this.queryTargets(owner, ar.placement, ar.spec.hit)) {
          if (!this.isLocal(t) || t.monster?.immunePush) continue;
          const dir = normalize({ x: ar.placement.origin.x - t.pos.x, z: ar.placement.origin.z - t.pos.z });
          t.pos = this.nav.slide(t.pos, { x: dir.x * ar.spec.pull * dt, z: dir.z * ar.spec.pull * dt });
        }
      }
      if (ar.t >= delay + duration) {
        ar.dead = true;
        this.emit({ type: 'areaEnd', id: ar.id });
      }
    }
    this.areas = this.areas.filter((a) => !a.dead);
  }

  // ------------------------------------------------------------------ main update

  update(dt: number): void {
    this.time += dt;
    this.tickCount++;
    this.aliveActors = [];
    for (const a of this.actors.values()) {
      a.prevPos.x = a.pos.x;
      a.prevPos.z = a.pos.z;
      if (a.alive) this.aliveActors.push(a);
    }
    this.spatial.rebuild(this.aliveActors);

    for (const a of this.aliveActors) {
      const local = this.isLocal(a);
      if (local) {
        updateStatuses(this, a, dt);
        if (!a.alive) continue;
        this.updateTimers(a, dt);
      } else if (a.statsDirty) {
        recomputeStats(a);
      }
      if (a.monster && local) {
        const brain = brains.get(registry.monsters.get(a.monster.defId)?.brain ?? 'melee');
        if (a.monster.countered > 0) a.monster.countered -= dt;
        else if (!a.hasStatus('staggered')) brain?.(this, a, dt);
      }
      if (a.player && local) {
        const cls = registry.classes.get(a.player.classId);
        cls?.identity.update?.(this, a, dt);
      }
    }

    for (const a of this.aliveActors) {
      if (a.cast) updateCast(this, a, dt);
      // Counter windows tick on every peer (they are opened by replicated casts).
      if (a.monster && a.monster.counterUntil > 0) a.monster.counterUntil = Math.max(0, a.monster.counterUntil - dt);
    }

    for (const a of this.aliveActors) {
      if (this.isLocal(a)) updateMovement(this, a, dt);
    }
    separate(this);

    this.updateProjectiles(dt);
    this.updateAreas(dt);

    for (const hook of this.tickHooks) hook(this, dt);
  }

  private updateTimers(a: Actor, dt: number): void {
    for (const [id, t] of a.cooldowns) {
      const nt = t - dt;
      if (nt > 0) {
        a.cooldowns.set(id, nt);
        continue;
      }
      a.cooldowns.delete(id);
      const s = a.skills.get(id);
      if (s?.charges && s.charges > 1) {
        const have = (a.charges.get(id) ?? s.charges) + 1;
        a.charges.set(id, Math.min(s.charges, have));
        if (have < s.charges) a.cooldowns.set(id, s.cooldown * (1 - a.stats.cdr));
      }
    }
    a.combatIdle += dt;
    const inCombat = a.combatIdle < 5;
    a.mp = Math.min(a.stats.maxMp, a.mp + (inCombat ? a.stats.mpRegenCombat : a.stats.mpRegenIdle) * dt);
    if (!inCombat && a.hp < a.stats.maxHp && a.player) a.hp = Math.min(a.stats.maxHp, a.hp + a.stats.maxHp * a.stats.hpRegen * dt);
    if (a.monster) {
      a.monster.age += dt;
      a.monster.idle += dt;
      const st = a.monster.stagger;
      if (st && !st.active && st.recoverTimer > 0) {
        st.recoverTimer -= dt;
        if (st.recoverTimer <= 0) {
          st.active = true;
          st.value = st.max;
        }
      }
    }
    const p = a.player;
    if (p) {
      if (p.standUpCd > 0) p.standUpCd = Math.max(0, p.standUpCd - dt);
      if (p.followUp && this.time > p.followUp.until && !a.cast) this.expireFollowUp(a);
      if (p.buffered) {
        if (this.time > p.buffered.until) p.buffered = null;
        else if (!a.cast || castCancelable(a.cast, false)) {
          const b = p.buffered;
          p.buffered = null;
          this.pressKey(a, b.key, b.aim);
        }
      }
    }
  }

  // ------------------------------------------------------------------ helpers for AI & scripts

  /** Nearest living enemy of `a` within range. */
  nearestEnemy(a: Actor, range: number): Actor | null {
    let best: Actor | null = null;
    let bestD = range;
    for (const t of this.spatial.query(a.pos, range)) {
      if (!t.alive || t.kind === 'npc' || !this.isEnemy(a, t)) continue;
      const d = dist(a.pos, t.pos);
      if (d < bestD) {
        best = t;
        bestD = d;
      }
    }
    return best;
  }

  playersInRange(p: V2, range: number): Actor[] {
    return this.aliveActors.filter((a) => a.kind === 'player' && dist(a.pos, p) <= range);
  }

  faceTowards(a: Actor, p: V2): void {
    a.facing = angleTo(a.pos, p);
  }

  /** Monster casts a skill toward a point (AI helper; host/owner only). */
  monsterCast(a: Actor, skillId: string, point: V2): CastState | null {
    let s = a.skills.get(skillId);
    if (!s) {
      const def = registry.skill(skillId);
      s = { ...def, level: 1, dmgMult: 1, critRateAdd: 0, critDmgAdd: 0, atkSpeedMult: 1, vsWeakMult: 1, vsBossMult: 1, defPen: 0, damageReduction: 0, identityGainMult: 1, extraStatus: [], tripods: [] };
      a.skills.set(skillId, s);
    }
    if ((a.cooldowns.get(skillId) ?? 0) > 0) return null;
    return startCast(this, a, s, point, {});
  }

  applyStatusTo(target: Actor, st: StatusApply, sourceId: EntityId): void {
    if (!this.isLocal(target)) return;
    applyStatus(this, target, st, sourceId, this.actors.get(sourceId)?.stats.attack ?? 0);
  }

  /**
   * Applies a status to any actor from host-side scripts: locally when this
   * peer owns the target, otherwise forwarded to the owner as a claim.
   */
  grantStatus(target: Actor, st: StatusApply, sourceId: EntityId): void {
    this.deliver(this.effectResult(target, sourceId, { statuses: [st] }));
  }

  /** Removes statuses from any actor (routed to its owner). */
  revokeStatus(target: Actor, ids: string[], sourceId: EntityId): void {
    this.deliver(this.effectResult(target, sourceId, { cleanse: ids }));
  }

  /** Heals / shields any actor (routed to its owner). */
  grantHeal(target: Actor, amount: number, sourceId: EntityId, shield = 0, shieldDuration = 4): void {
    this.deliver(this.effectResult(target, sourceId, { heal: amount, shield, shieldDuration }));
  }

  private effectResult(target: Actor, sourceId: EntityId, extra: Partial<HitResult>): HitResult {
    return {
      srcId: sourceId, tgtId: target.id, amount: 0, crit: false, back: false, head: false, counter: false, stagger: 0, weakPoint: 0,
      element: 'none', statuses: [], reaction: null, from: { ...target.pos }, srcAttack: this.actors.get(sourceId)?.stats.attack ?? 0,
      heal: 0, shield: 0, shieldDuration: 0, friendly: true, skillId: null, isDot: false, ...extra,
    };
  }

  hardCCAllowed(t: Actor): boolean {
    return hardCCAllowed(t);
  }
}
